import * as React from "react";
import * as Frontend from "kmmp/frontend";
import { useWhitelabel } from "client/hooks";
import { PortraitFormDispatch, PortraitFormState } from "./reducers";
import { Option } from "@nozzlegear/railway";
import { getFrames, getPrestigePortraitFrameImages } from "client/image-urls";
import { PurchaseOrderIdBox } from "./purchase_order_id_box";
import { FrameSelector } from "client/pages/quick/frame_size_selector";
import { If } from "shared/components";

type Props = {
    requireCoupaPurchaseOrder: boolean
    signaturePortraitsOnly: boolean
    backgrounds: Frontend.BackgroundImage[]
    state: PortraitFormState
    dispatch: PortraitFormDispatch
}

export function PortraitFormControls(props: Props): React.ReactElement {
    const { state, dispatch, backgrounds } = props;
    const { frameVariant, backgroundVariant, orientation, purchaseOrderId } = state;

    const { companyName } = useWhitelabel();

    const validFrames = React.useMemo(() => {
        return props.signaturePortraitsOnly === true
            ? getPrestigePortraitFrameImages(companyName)
            : getFrames(companyName);
    }, [props.signaturePortraitsOnly, companyName]);

    const validFrameSizes = React.useMemo<Frontend.FrameSize[]>(() => {
        if (props.signaturePortraitsOnly) {
            return ["16x20"];
        }

        switch (companyName) {
            case "sci":
            case "church-and-chapel":
                return ["16x20", "11x14", "8x10"];

            // TODO: unclear if all companies should have access to all three sizes
            default:
                return ["16x20", "11x14", "8x10"];
        }
    }, [props.signaturePortraitsOnly, companyName]);

    const selectFrame = (event: React.FormEvent<any>) => {
        const val = (event.target as any as HTMLSelectElement).value;
        const frame = validFrames.find((f) => f.name === val) || validFrames[0];

        dispatch({ type: "setFrame", value: frame });
    };

    const selectFrameSize = (size: Frontend.FrameSize) =>
        dispatch({type: "setFrameSize", value: size});

    const selectBg = (event: React.FormEvent<any>) => {
        const val = (event.target as any as HTMLSelectElement).value;
        const bg = backgrounds.find((bg) => bg.name === val);

        if (!bg)
            return;

        dispatch({ type: "setBackground", value: bg });
    };

    const selectOrientation = (event: React.FormEvent<any>) => {
        const val = (event.target as any as HTMLSelectElement).value as "portrait" | "landscape";
        dispatch({ type: "setOrientation", value: val });
    };

    const setPoId = (purchaseOrderId: string) => {
        dispatch({ type: "setPurchaseOrderId", value: purchaseOrderId });
    };

    const bgs = backgrounds.map((bg) => (
        <option key={bg.name} value={bg.name}>
            {bg.name}
        </option>
    ));

    const frames = validFrames.map((f) => (
        <option key={f.name} value={f.name}>
            {f.name}
        </option>
    ));

    const orientations = [
        { value: "portrait", description: "Portrait (vertical)" },
        { value: "landscape", description: "Landscape (horizontal)" },
    ].map((o) => (
        <option key={o.value} value={o.value}>
            {o.description}
        </option>
    ));

    return (
        <div key={"portrait-form-controls"}>
            <If condition={props.requireCoupaPurchaseOrder}>
                <PurchaseOrderIdBox value={purchaseOrderId} onChange={setPoId} />
            </If>

            <FrameSelector
                company={companyName}
                showForCompanies={["sci", "messenger", "church-and-chapel"]}
                selectedSize={state.frameSize}
                validSizes={validFrameSizes}
                onChange={selectFrameSize}
            />

            <div className="selector-container">
                <p>{"Frame:"}</p>
                <select
                    autoComplete="off"
                    className="form-control selector"
                    value={frameVariant.map<string>((i) => i.name).defaultValue("")}
                    onChange={selectFrame}
                >
                    {Option.isNone(frameVariant) ? <option value="">{`-- Select a frame --`}</option> : null}
                    {frames}
                </select>
            </div>
            <div className="selector-container">
                <p>{"Background:"}</p>
                <select
                    autoComplete="off"
                    className="form-control selector"
                    value={backgroundVariant.name}
                    onChange={selectBg}
                >
                    {bgs}
                </select>
            </div>
            <div className="selector-container">
                <p>{"Orientation:"}</p>
                <select
                    autoComplete="off"
                    className="form-control selector"
                    value={orientation.map<string>((o) => o).defaultValue("")}
                    onChange={selectOrientation}
                >
                    {Option.isNone(orientation) ? <option value="">{`-- Select an orientation --`}</option> : null}
                    {orientations}
                </select>
            </div>
        </div>
    );
};

